import React, { useState, useEffect } from 'react';
import { Calendar, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ReportingPeriod = ({ onSubmit, initialData }) => {
  const { t } = useTranslation();
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [financialYearLabel, setFinancialYearLabel] = useState('');
  const [errors] = useState({});
  const [warnings] = useState({});

  // Load data from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('scopeBoundaries_reportingPeriod');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        if (parsedData.periodFrom) setPeriodFrom(parsedData.periodFrom);
        if (parsedData.periodTo) setPeriodTo(parsedData.periodTo);
        if (parsedData.financialYearLabel) setFinancialYearLabel(parsedData.financialYearLabel);
      } catch (error) {
        console.error('Error loading reporting period data:', error);
      }
    }
  }, []);

  // Validation helpers kept; navigation handled at WorkflowTabView level

  // Navigation handled by WorkflowTabView; save via onSubmit externally

  const setPresetPeriod = (preset) => {
    const today = new Date();
    const currentYear = today.getFullYear();
    
    if (preset === 'calendar_year') {
      setPeriodFrom(`${currentYear}-01-01`);
      setPeriodTo(`${currentYear}-12-31`);
      setFinancialYearLabel(`FY${currentYear}`);
    } else if (preset === 'last_year') {
      setPeriodFrom(`${currentYear - 1}-01-01`);
      setPeriodTo(`${currentYear - 1}-12-31`);
      setFinancialYearLabel(`FY${currentYear - 1}`);
    }
  };

  const getYearFromDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).getFullYear();
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6">
        <div className="flex items-center mb-4">
          <Calendar className="h-6 w-6 text-blue-600 mr-3" />
          <h2 className="text-xl font-semibold text-gray-900">{t('workflow.scopeBoundaries.reportingPeriod.title')}</h2>
        </div>
        <p className="text-gray-600">{t('workflow.scopeBoundaries.reportingPeriod.description')}</p>
      </div>

      {/* Presets */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('workflow.scopeBoundaries.reportingPeriod.quickPresets')}
        </label>
        <div className="flex space-x-3">
          <button
            onClick={() => setPresetPeriod('calendar_year')}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {t('workflow.scopeBoundaries.reportingPeriod.calendarYear')}
          </button>
          <button
            onClick={() => setPresetPeriod('last_year')}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {t('workflow.scopeBoundaries.reportingPeriod.lastYear')}
          </button>
        </div>
      </div>

      {/* Date Range */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Start Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('workflow.scopeBoundaries.reportingPeriod.form.startDate')}
          </label>
          <input
            type="date"
            value={periodFrom}
            onChange={(e) => setPeriodFrom(e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.periodFrom ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.periodFrom && (
            <p className="mt-1 text-sm text-red-600">{errors.periodFrom}</p>
          )}
          {warnings.periodFrom && (
            <p className="mt-1 text-sm text-yellow-600 flex items-center">
              <AlertTriangle className="h-4 w-4 mr-1" />
              {t('workflow.scopeBoundaries.reportingPeriod.warnings.futureDate')}
            </p>
          )}
        </div>

        {/* End Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('workflow.scopeBoundaries.reportingPeriod.form.endDate')}
          </label>
          <input
            type="date"
            value={periodTo}
            onChange={(e) => setPeriodTo(e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.periodTo ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.periodTo && (
            <p className="mt-1 text-sm text-red-600">{errors.periodTo}</p>
          )}
          {warnings.periodTo && (
            <p className="mt-1 text-sm text-yellow-600 flex items-center">
              <AlertTriangle className="h-4 w-4 mr-1" />
              {t('workflow.scopeBoundaries.reportingPeriod.warnings.endBeforeStart')}
            </p>
          )}
        </div>
      </div>

      {/* Financial Year Label */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('workflow.scopeBoundaries.reportingPeriod.form.financialYearLabel')}
        </label>
        <input
          type="text"
          value={financialYearLabel}
          onChange={(e) => setFinancialYearLabel(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('workflow.scopeBoundaries.reportingPeriod.form.financialYearLabelPlaceholder')}
        />
      </div>

      {/* Period Summary */}
      {periodFrom && periodTo && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Period Summary</h3>
          <div className="text-sm text-gray-600">
            <p><strong>Duration:</strong> {periodFrom} to {periodTo}</p>
            <p><strong>Year:</strong> {getYearFromDate(periodFrom)}</p>
            {financialYearLabel && (
              <p><strong>Financial Year:</strong> {financialYearLabel}</p>
            )}
          </div>
        </div>
      )}

      {/* No navigation here; handled by WorkflowTabView */}
    </div>
  );
};

export default ReportingPeriod;
